import {
	Button,
	Checkbox,
	FormControl,
	Grid,
	Input,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	TextField,
	Theme,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import ShopLocationSelect from 'components/ShopLocationSelect';
import React from 'react';
import { useTranslation } from 'services/localization';
import { AccountingTableName, TableSection } from '../types';
import {
	allAccountingTables,
	allTableSections,
	isAccountingGroupFilterApplied,
	othersAccountingGroup,
} from '../utils';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';
import {
	getAccountingTableNameTranslation,
	getAccountingTableSectionTranslation,
} from 'services/translations';

interface Props {
	availableCurrencies: string[];
	currency: string;
	setCurrency: (currency: string) => void;
	locationId: string;
	setLocationId: (locationId: string) => void;
	tablesToShow: AccountingTableName[];
	setTablesToShow: (tables: AccountingTableName[]) => void;
	tableSectionsToShow: TableSection[];
	setTableSectionsToShow: (sections: TableSection[]) => void;
	accountingGroupIdsToShow: string[];
	setAccountingGroupIdsToShow: (ids: string[]) => void;
	printReport: () => void;
}

const HeaderBar = (props: Props) => {
	const {
		availableCurrencies,
		currency,
		setCurrency,
		locationId,
		setLocationId,
		tablesToShow,
		setTablesToShow,
		tableSectionsToShow,
		setTableSectionsToShow,
		accountingGroupIdsToShow,
		setAccountingGroupIdsToShow,
		printReport,
	} = props;
	const { t } = useTranslation();
	const classes = useStyles();
	const customAccountingGroups = useSelector(ShopSelectors.customAccountingGroups);
	const shopHasCustomAccountingGroups = !!customAccountingGroups.length;
	const tableSections = allTableSections.filter((s) =>
		s === 'custom_group_breakdown' && !shopHasCustomAccountingGroups ? false : true,
	);

	const allAccountingGroups = [...customAccountingGroups, othersAccountingGroup(t)];

	const accountingGroupFilterApplied = isAccountingGroupFilterApplied(
		accountingGroupIdsToShow,
		allAccountingGroups,
	);

	const getDisabledIfAccountingGroupFilterHelperText = () =>
		t(
			'reports.accounting.disabledIfAccountingGroupFilter',
			'Hidden if accounting group filter is applied',
		);

	const tableSectionOptions = tableSections.map((section) => {
		const isTableSectionDisabledBecauseOfAccountingGroupFilter =
			section === 'method_breakdown' && !!accountingGroupFilterApplied;
		return {
			name: section,
			disabled: isTableSectionDisabledBecauseOfAccountingGroupFilter,
			disabledText: isTableSectionDisabledBecauseOfAccountingGroupFilter
				? getDisabledIfAccountingGroupFilterHelperText()
				: undefined,
		};
	});

	const tableOptions = allAccountingTables.map((table) => {
		const isTableDisabledBecauseOfAccountingGroupFilter =
			table === 'fees' && !!accountingGroupFilterApplied;
		return {
			name: table,
			disabled: isTableDisabledBecauseOfAccountingGroupFilter,
			disabledText: isTableDisabledBecauseOfAccountingGroupFilter
				? getDisabledIfAccountingGroupFilterHelperText()
				: undefined,
		};
	});

	return (
		<div className={classNames(classes.actionBar, classes.hideOnPrint)}>
			<Grid container className={classes.filterBar} spacing={2}>
				<Grid item>
					<TextField
						id="currency"
						name="currency"
						variant="standard"
						select
						value={currency}
						label={t('common:payment.currency')}
						onChange={(e) => setCurrency(e.target.value)}
						SelectProps={{
							displayEmpty: true,
							autoWidth: true,
						}}
						InputLabelProps={{
							shrink: true,
						}}
						InputProps={{
							disableUnderline: true,
						}}
						margin="none"
						type="text"
					>
						{availableCurrencies.map((currency) => (
							<MenuItem value={currency} key={currency}>
								{currency}
							</MenuItem>
						))}
					</TextField>
				</Grid>
				<Grid item>
					<ShopLocationSelect
						value={locationId}
						onChange={(value) => setLocationId(value)}
						showLabel
						hideIcon
					/>
				</Grid>
				<Grid item>
					<FormControl className={classes.formControl}>
						<InputLabel id="tables-select" variant="standard">
							{t(`reports.accounting.tables`, 'Tables')}
						</InputLabel>
						<Select
							labelId="tables-select"
							variant="standard"
							multiple
							value={tablesToShow}
							onChange={(e) => setTablesToShow(e.target.value as AccountingTableName[])}
							input={<Input />}
							renderValue={(selected) =>
								(selected as AccountingTableName[])
									.map((name) => getAccountingTableNameTranslation(name, t))
									.join(', ')
							}
							disableUnderline
						>
							{tableOptions.map(({ name, disabled, disabledText }) => (
								<MenuItem key={name} value={name} disabled={disabled}>
									<Checkbox color="primary" checked={tablesToShow.includes(name)} />
									<ListItemText
										primary={getAccountingTableNameTranslation(name, t)}
										secondary={disabledText}
									/>
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item>
					<FormControl className={classes.formControl}>
						<InputLabel id="sections-select" variant="standard">
							{t(`reports.accounting.tableSections`, 'Table sections')}
						</InputLabel>
						<Select
							labelId="sections-select"
							variant="standard"
							multiple
							value={tableSectionsToShow}
							onChange={(e) => setTableSectionsToShow(e.target.value as TableSection[])}
							input={<Input />}
							renderValue={(selected) =>
								(selected as TableSection[])
									.map((name) => getAccountingTableSectionTranslation(name, t))
									.join(', ')
							}
							disableUnderline
						>
							{tableSectionOptions.map(({ name, disabled, disabledText }) => (
								<MenuItem key={name} value={name} disabled={disabled}>
									<Checkbox color="primary" checked={tableSectionsToShow.includes(name)} />
									<ListItemText
										primary={getAccountingTableSectionTranslation(name, t)}
										secondary={disabledText}
									/>
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				{shopHasCustomAccountingGroups && (
					<Grid item>
						<FormControl className={classes.formControl}>
							<InputLabel id="accounting-groups-select">
								{t(`reports.accounting.accountingGroups`, 'Accounting groups')}
							</InputLabel>
							<Select
								labelId="accounting-groups-select"
								multiple
								value={accountingGroupIdsToShow}
								onChange={(e) => setAccountingGroupIdsToShow(e.target.value as string[])}
								input={<Input />}
								renderValue={(selected) =>
									(selected as string[])
										.map((id) => allAccountingGroups.find((group) => group.id === id)?.name ?? '')
										.join(', ')
								}
								disableUnderline
							>
								{allAccountingGroups.map((accountingGroup) => (
									<MenuItem key={accountingGroup.id} value={accountingGroup.id}>
										<Checkbox
											color="primary"
											checked={accountingGroupIdsToShow.includes(accountingGroup.id)}
										/>
										<ListItemText primary={accountingGroup.name} />
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				)}
			</Grid>
			<Button
				className={classes.printBtn}
				variant="contained"
				color="primary"
				onClick={printReport}
			>
				{t('common:actions.print', 'Print')}
			</Button>
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		actionBar: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
		},
		filterBar: {
			flex: 1,
		},
		formControl: {
			maxWidth: 180,
			minWidth: 120,
		},
		printBtn: {},
		hideOnPrint: {
			'@media print': {
				display: 'none',
			},
		},
	}),
);

export default HeaderBar;
